import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { CreditPurchaseForm } from '../components/CreditPurchaseForm';
import { useCredits } from '../contexts/CreditContext';

const CreditPurchasePage: React.FC = () => {
  const { t } = useTranslation();
  const { refreshCredits } = useCredits();
  

  const handlePurchaseSuccess = async () => {
    await refreshCredits();
  };


  return (
    <Box sx={{ backgroundColor: '#0f0f23', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header />
      
      {/* Hero Section */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, rgba(40, 160, 240, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
        borderBottom: '1px solid rgba(40, 160, 240, 0.2)',
        py: 6
      }}>
        <Container maxWidth="lg" sx={{ width: '80%' }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <CreditCardIcon sx={{ 
              fontSize: 64, 
              color: '#28A0F0', 
              mb: 2,
              filter: 'drop-shadow(0 0 20px rgba(40, 160, 240, 0.3))'
            }} />
            <Typography 
              variant="h2" 
              sx={{ 
                color: '#ffffff', 
                fontWeight: 700, 
                mb: 2,
                background: 'linear-gradient(135deg, #28A0F0 0%, #EC4899 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {t('credits.purchase.pageTitle')}
            </Typography>
            <Typography 
              variant="h5" 
              sx={{ 
                color: '#a0a0a0', 
                fontWeight: 400,
                maxWidth: 600,
                mx: 'auto'
              }}
            >
              {t('credits.purchase.pageSubtitle')}
            </Typography>
          </Box>

          {/* Features */}
          <Stack 
            direction={{ xs: 'column', md: 'row' }} 
            spacing={3} 
            justifyContent="center"
            sx={{ mb: 4 }}
          >
            <Card sx={{ 
              background: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 3,
              p: 2,
              textAlign: 'center',
              flex: 1,
              maxWidth: 300
            }}>
              <CardContent>
                <SecurityIcon sx={{ color: '#10b981', fontSize: 40, mb: 1 }} />
                <Typography variant="h6" sx={{ color: '#ffffff', mb: 1 }}>
                  {t('credits.purchase.features.secure')}
                </Typography>
                <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                  {t('credits.purchase.features.secureDesc')}
                </Typography>
              </CardContent>
            </Card>

            <Card sx={{ 
              background: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 3,
              p: 2,
              textAlign: 'center',
              flex: 1,
              maxWidth: 300
            }}>
              <CardContent>
                <SpeedIcon sx={{ color: '#fbbf24', fontSize: 40, mb: 1 }} />
                <Typography variant="h6" sx={{ color: '#ffffff', mb: 1 }}>
                  {t('credits.purchase.features.instant')}
                </Typography>
                <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                  {t('credits.purchase.features.instantDesc')}
                </Typography>
              </CardContent>
            </Card>

            <Card sx={{ 
              background: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 3,
              p: 2,
              textAlign: 'center',
              flex: 1,
              maxWidth: 300
            }}>
              <CardContent>
                <CheckCircleIcon sx={{ color: '#3b82f6', fontSize: 40, mb: 1 }} />
                <Typography variant="h6" sx={{ color: '#ffffff', mb: 1 }}>
                  {t('credits.purchase.features.reliable')}
                </Typography>
                <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                  {t('credits.purchase.features.reliableDesc')}
                </Typography>
              </CardContent>
            </Card>
          </Stack>
        </Container>
      </Box>

      {/* Purchase Form Section */}
      <Box sx={{ py: 6, flex: 1 }}>
        <Container maxWidth="lg" sx={{ width: '80%' }}>
          <Card sx={{
            background: 'rgba(255, 255, 255, 0.02)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 4,
            overflow: 'hidden',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
          }}>
            <CardContent sx={{ p: 4 }}>
              <CreditPurchaseForm onSuccess={handlePurchaseSuccess} />
            </CardContent>
          </Card>
        </Container>
      </Box>
      
      <Footer />
    </Box>
  );
};

export default CreditPurchasePage;
